import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, inject, Input, isDevMode, TemplateRef, ViewContainerRef } from '@angular/core';
import type { Subscription } from 'rxjs';
import { RolesService } from '../roles.service';

/**
 * Structural directive to show/hide content based on permission(s).
 *
 * Usage:
 *   <button *appHasPermission="'action:patients:update'">Edit</button>
 *   <div *appHasPermission="['route:patients','action:patients:view']">...</div>
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly roles = inject(RolesService);
  private hasView = false;
  private sub: Subscription | null = null;
  private currentPermission: string | string[];

  @Input('appHasPermission') set permission(value: string | string[]) {
    this.currentPermission = value;
    this.evaluateAndRender();
  }

  public ngOnInit(): void {
    // Re-evaluate on role changes so UI updates when RBAC Debug toggles roles
    this.sub = this.roles.currentRole$.subscribe(() => {
      this.evaluateAndRender();
    });
  }

  public ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private showView(): void {
    if (this.hasView) {
      return;
    }
    this.viewContainer.clear();
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.hasView = true;
  }

  private hideView(): void {
    if (!this.hasView) {
      return;
    }
    this.viewContainer.clear();
    this.hasView = false;
  }

  private evaluateAndRender(): void {
    const value = this.currentPermission;
    if (value == null) {
      return;
    }
    const allowed = Array.isArray(value) ? value.every((p) => this.roles.canAccess(p)) : this.roles.canAccess(value);
    if (this.isDebugEnabled()) {
      console.debug('[RBAC] appHasPermission', value, '=>', allowed);
    }
    if (allowed) {
      this.showView();
      return;
    }
    this.hideView();
  }

  private isDebugEnabled(): boolean {
    if (isDevMode()) {
      return true;
    }
    try {
      return localStorage.getItem('rbac_debug') === '1';
    } catch {
      return false;
    }
  }
}
